import type { ReactNode } from "react";

/**
 * Themed screen title — a gold icon badge + heading, shared by the گفتگو /
 * دوستان / پروفایل headers so the top-level tabs read as one family instead of
 * bare words. Tokens only (`secondary-container` resolves per theme), RTL-safe
 * (a plain flex row follows the document direction).
 */
export type ScreenTitleIcon =
  | "chat"
  | "friends"
  | "profile"
  | "mail"
  | "store"
  | "settings"
  | "help"
  | "history"
  | "blocked"
  | "leaderboard";

const GLYPH: Record<ScreenTitleIcon, string> = {
  // trophy — the same glyph the bottom-nav tab uses, so the tab and the heading read as one thing
  leaderboard:
    "M7 3h10v6a5 5 0 0 1-10 0V3zM5.5 4.2H3v2.1a3.6 3.6 0 0 0 3.1 3.6l.5-2A1.6 1.6 0 0 1 5 6.3V4.2zm13 0H21v2.1a3.6 3.6 0 0 1-3.1 3.6l-.5-2A1.6 1.6 0 0 0 19 6.3V4.2zM11 14.4h2v2.9h-2v-2.9zM7.6 21l.8-2.6c.1-.4.5-.7.9-.7h5.4c.4 0 .8.3.9.7l.8 2.6H7.6z",
  chat: "M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM8 11a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 8 11zm4 0a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 12 11zm4 0a1.25 1.25 0 1 1 0-2.5A1.25 1.25 0 0 1 16 11z",
  friends:
    "M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
  profile:
    "M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  mail: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z",
  store: "M20 4H4v2h16V4zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6h1zm-9 4H6v-4h6v4z",
  settings:
    "M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7 7 0 0 0-1.63-.94l-.36-2.54a.5.5 0 0 0-.5-.42h-3.84a.5.5 0 0 0-.5.42l-.36 2.54c-.59.24-1.13.56-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.74 8.84a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.42.34.6.22l2.39-.96c.5.38 1.04.7 1.63.94l.36 2.54c.05.24.25.42.5.42h3.84c.25 0 .45-.18.5-.42l.36-2.54c.59-.24 1.13-.56 1.63-.94l2.39.96c.18.12.46.02.6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z",
  help: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z",
  history:
    "M13 3a9 9 0 0 0-9 9H1l3.9 3.9.1.14L9 12H6a7 7 0 1 1 2.05 4.95l-1.42 1.42A9 9 0 1 0 13 3zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z",
  // The slashed circle — the block glyph, matching the one the DM thread header uses.
  blocked:
    "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.94 7.94 0 0 1 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.94 7.94 0 0 1 20 12c0 4.42-3.58 8-8 8z",
};

function TitleGlyph({ icon }: { icon: ScreenTitleIcon }): ReactNode {
  return (
    <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
      <path d={GLYPH[icon]} />
    </svg>
  );
}

export function ScreenTitle({ icon, title }: { icon: ScreenTitleIcon; title: string }): ReactNode {
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden="true"
        className="flex h-9 w-9 flex-none items-center justify-center rounded-xl border border-gold-deep/30 bg-secondary-container text-on-secondary-container shadow-sm"
      >
        <TitleGlyph icon={icon} />
      </span>
      <h1 className="m-0 truncate font-headline-md text-headline-md tracking-tight text-secondary">
        {title}
      </h1>
    </span>
  );
}
